import {api} from './api'
import type {
    Bill,
    BillsResponse,
    ListBillsParams,
    UpdateBillWorkflowRequest,
    ProcessBillActionRequest
} from '@/types/bill'
import type {CreateJobResponse} from '@/types/job'
import type {MonitorCostRollup} from '@/types/monitor-cost'
import {useMutation} from '@tanstack/react-query'

const transformBillResponse = (data: any): Bill => ({
    id: data.SK,
    billNumber: data.SK,
    title: data.Title,
    synopsis: data.Synopsis,
    status: data.SecondReadingActionHouseOfOrigin || 'Unknown',
    chamber: data.Chamber,
    crossfileBillNumber: data.CrossfileBillNumber,
    session: data.Session,
    lastModified: data.LastProcessedTime || new Date().toISOString(),
    url: data.PDFURL,
    workflowStatus: data.BillWorkflowStatus,
    fullTextAvailable: data.FullTextAvailable,
    monitor_analysis: data.MonitorAnalysis,
    versions: Array.isArray(data.Versions) ? data.Versions.map((v: any) => ({
        version: v.Version,
        md5Hash: v.MD5Hash,
        createdAt: v.CreatedAt,
        pdfUploaded: v.PDFUploaded,
        textExtracted: v.TextExtracted,
        textractJobId: v.TextractJobId,
        pdfS3Key: v.PDFS3Key,
        textS3Key: v.TextS3Key,
        legislativeReviewS3Key: v.LegislativeReviewS3Key,
        refinementS3Key: v.RefinementS3Key,
        oppositionS3Key: v.OppositionS3Key,
        supportS3Key: v.SupportS3Key,
        shouldMonitorS3Key: v.ShouldMonitorS3Key
    })) : []
})

export const billsApi = {
    getBill: async (session: string, billNumber: string): Promise<Bill> => {
        const encodedSession = encodeURIComponent(session)
        try {
            const response = await api.get<any>(`/bills/${encodedSession}/${billNumber}`)
            return transformBillResponse(response.data)
        } catch (error) {
            console.error('Failed to fetch bill:', error)
            throw error
        }
    },

    getPresignedUrl: async (s3Key: string): Promise<string> => {
        try {
            const response = await api.get<{ url: string }>(`/bills/files/${s3Key}`)
            return response.data.url
        } catch (error) {
            console.error('Failed to get presigned URL:', error)
            throw error
        }
    },

    listBills: async ({session, nextToken, workflowStatus}: ListBillsParams): Promise<BillsResponse> => {
        const params = new URLSearchParams()
        if (nextToken) {
            const tokenStr = typeof nextToken === 'object' ? JSON.stringify(nextToken) : nextToken
            params.append('next_token', tokenStr)
        }
        if (workflowStatus) params.append('workflow_status', workflowStatus)

        const encodedSession = encodeURIComponent(session)

        try {
            const response = await api.get<any>(`/bills/${encodedSession}?${params.toString()}`)
            console.log('Raw response data:', response.data)

            const transformedResponse: BillsResponse = {
                items: Array.isArray(response.data.items)
                    ? response.data.items.map(transformBillResponse)
                    : [],
                nextToken: response.data.next_token
            }

            console.log('Transformed response:', transformedResponse)
            return transformedResponse
        } catch (error: any) {
            console.error('Failed to fetch bills:', error.message)
            throw error
        }
    },

    updateBillWorkflow: async ({session, billNumber, workflowStatus}: UpdateBillWorkflowRequest): Promise<Bill> => {
        const encodedSession = encodeURIComponent(session)
        const response = await api.put<any>(`/bills/${encodedSession}/${billNumber}`, {
            BillWorkflowStatus: workflowStatus
        })
        return transformBillResponse(response.data)
    },

    getBillVersion: async (session: string, billNumber: string, version: number): Promise<ArrayBuffer> => {
        const encodedSession = encodeURIComponent(session)
        const response = await api.get(`/bills/${encodedSession}/${billNumber}/versions/${version}`, {
            responseType: 'arraybuffer'
        })
        return response.data
    },

    syncBills: async (session: string): Promise<CreateJobResponse> => {
        try {
            const response = await api.post<CreateJobResponse>('/jobs/sync-bills', {
                session: session.toLowerCase()
            })
            return response.data
        } catch (error) {
            console.error('Failed to sync bills:', error)
            throw error
        }
    },

    processBillAction: async ({session, billNumber, action}: ProcessBillActionRequest): Promise<CreateJobResponse> => {
        const encodedSession = encodeURIComponent(session)
        const response = await api.post<CreateJobResponse>(
            `/jobs/process-bills/${encodedSession}/${billNumber}?bill_action=${action}`
        )
        return response.data
    },

    getSessionMonitorCostEstimate: async (session: string): Promise<MonitorCostRollup> => {
        const encodedSession = encodeURIComponent(session)
        const response = await api.get<MonitorCostRollup>(`/bills/${encodedSession}/cost-estimate`)
        return response.data
    },
    updateAllBillStates: async (): Promise<any> => {
        const response = await api.post<any>(`/config/update-states`)
        return response.data
    },

    bulkPrintReviews: async (session: string, s3Keys: string[]): Promise<{ url: string, processed_files: number, total_keys: number, missing_reviews: string[] }> => {
        try {
            console.log(`Making bulk-print request for ${s3Keys.length} file keys in session ${session}`)
            const response = await api.post<{ url: string, processed_files: number, total_keys: number, missing_reviews: string[] }>(
                '/bills/bulk-print-reviews',
                { session, s3_keys: s3Keys }
            )
            console.log('Bulk print API response:', response.data)
            return response.data
        } catch (error) {
            console.error('Failed to generate bulk print PDF:', error)
            throw error
        }
    },

    getAIReview: async (session: string, billNumber: string): Promise<any> => {
        try {
            const encodedSession = encodeURIComponent(session)
            const response = await api.get<any>(`/bills/ai-review/${encodedSession}/${billNumber}`)
            return response.data
        } catch (error) {
            console.error('Failed to fetch AI review:', error)
            throw error
        }
    }
}


export const useSyncBills = () => {
    return useMutation({
        mutationFn: billsApi.syncBills
    })
}

export const useProcessBillAction = () => {
    return useMutation({
        mutationFn: billsApi.processBillAction
    })
}

export const useGetPresignedUrl = () => {
    return useMutation({
        mutationFn: billsApi.getPresignedUrl
    })
}

export const useBulkPrintReviews = () => {
    return useMutation({
        mutationFn: ({ session, s3Keys }: { session: string, s3Keys: string[] }) =>
            billsApi.bulkPrintReviews(session, s3Keys)
    })
}

export const useGetAIReview = () => {
    return useMutation({
        mutationFn: ({ session, billNumber }: { session: string, billNumber: string }) =>
            billsApi.getAIReview(session, billNumber)
    })
}